// src/screens/mypage/StoreAdminFaqScreen.js
// 신규: 매장관리자 마이탭 "운영 Q&A" — 자주 나오는 질문을 자가 해결하게 해서 본사 문의를
// 줄이는 목적. 질문을 누르면 아코디언으로 펼쳐지며 답변(단계별 텍스트, 일부는 실제 화면
// 캡처 포함)이 보인다. 텍스트를 기본으로 하고(화면이 바뀌어도 문구만 고치면 되어 유지보수
// 부담이 적음), 이미 촬영해둔 화면 캡처가 있는 항목(휴업/운영시간·PICK/예약취소/플랜 확인)만
// 보조로 이미지를 붙였다 — 새 캡처를 전부 찍기보다 기존 자산을 재활용.
//
// 가독성 개선: 첫 버전은 답변을 한 문단(단계 설명+주의사항이 뒤섞인 긴 텍스트)으로 담아
// 한눈에 안 들어온다는 피드백 — steps(번호 매긴 실행 순서)와 note(별도 강조 박스의
// 참고/주의사항)를 분리해서, "뭘 눌러야 하는지"와 "알아두면 좋은 것"이 시각적으로
// 구분되게 재구성. 이미지도 resizeMode:cover로 잘려 보이던 것을 contain으로 바꿔
// 화면 전체가 잘리지 않고 다 보이게 함.
//
// 80차: 항목이 16개까지 늘면서 하나의 긴 세로 목록이라 원하는 질문을 찾으려면 계속
// 스크롤해야 해 가독성이 떨어진다는 피드백 — 질문마다 category를 부여하고 상단에
// 가로 탭(칩)을 둬서, 유형별로 바로 필터링해 원하는 질문만 보게 재구성. 카테고리는
// "무엇을 하려는 상황인가" 기준으로 5개로 나눔(매장 운영/테마 관리/예약 설정/예약 관리/
// 고객 소통) — 질문/답변 문구 자체는 기존 그대로 유지, category 필드만 추가.
import React, { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Image } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { COLORS } from '../../constants/theme';
import AppHeader from '../../components/common/AppHeader';
import { HelpCircleIcon, IconLabel } from '../../components/common/Icons';

const GOLD = COLORS.gold, BG = COLORS.bg, SURFACE2 = COLORS.surface2, SURFACE = COLORS.surface;
const BORDER = COLORS.border, TEXT = COLORS.text, TEXT_MUTED = COLORS.textMuted;

const CATEGORIES = [
  { key: 'store',   label: '매장 운영' },
  { key: 'theme',   label: '테마 관리' },
  { key: 'booking_settings', label: '예약 설정' },
  { key: 'booking_ops',      label: '예약 관리' },
  { key: 'customer', label: '고객 소통' },
];

const FAQ_ITEMS = [
  {
    category: 'store',
    q: '매장을 잠시 휴업하려면 어떻게 하나요?',
    steps: [
      '마이 탭 → 매장 정보로 이동',
      '여러 매장을 관리 중이면 대상 매장 선택',
      '"일시 휴업" 토글 켜기',
    ],
    note: '휴업 중엔 새 예약이 막혀요. 이미 잡힌 예약은 자동으로 취소되지 않으니, 필요하면 고객에게 직접 안내해주세요. 다시 영업하려면 같은 토글을 꺼주면 됩니다.',
    image: require('../../../assets/guide/faq_pause.png'),
  },
  {
    category: 'theme',
    q: '테마를 새로 추가/수정/삭제하고 싶어요.',
    steps: [
      '마이 탭 → 본사에 문의하기로 이동',
      '카테고리에서 "테마추가 / 테마수정 / 테마삭제" 중 선택',
      '테마명·설명·난이도·장르·플레이타임·휴게시간 입력 (인원별 가격은 선택)',
      '테마 사진 첨부 후 접수',
    ],
    note: '테마 등록·수정·삭제는 총괄관리자만 반영할 수 있어요. 접수하면 확인 후 처리해드립니다.',
    image: null,
  },
  {
    category: 'booking_settings',
    q: '운영시간(예약 가능 시간)은 어떻게 설정하나요?',
    steps: [
      '운영관리 탭 → 매장 설정 → 운영시간으로 이동',
      '시작/종료 시간 입력',
      '특정 테마만 다르게 하려면 그 테마 카드를 펼쳐 개별 설정',
    ],
    note: '저장하면 예약 가능한 시간이 자동으로 만들어져요.',
    image: require('../../../assets/guide/faq_settings.jpg'),
  },
  {
    category: 'theme',
    q: 'PICK 테마는 어떻게 등록하나요?',
    steps: [
      '운영관리 탭 → 매장 설정 → PICK 테마로 이동',
      '노출할 테마 선택',
      '노출 기간 설정 (최대 90일)',
    ],
    note: 'PREMIUM 플랜 매장만 이용할 수 있어요.',
    image: require('../../../assets/guide/faq_settings.jpg'),
  },
  {
    category: 'booking_ops',
    q: '전화나 다른 사이트로 받은 예약을 앱에도 반영하고 싶어요.',
    steps: [
      '운영관리 탭 → 예약 관리로 이동',
      '우측 상단 연필 아이콘 탭',
      '예약 정보 입력 후 등록',
    ],
    note: '전화·타 플랫폼으로 받은 예약을 미리 반영해두면 같은 시간에 중복 예약되는 걸 막을 수 있어요.',
    image: null,
  },
  {
    category: 'booking_ops',
    q: '예약을 취소하고 싶어요.',
    steps: [
      '운영관리 탭 → 예약 관리로 이동',
      '취소할 예약 카드에서 "취소" 버튼 탭',
    ],
    note: '고객이 포인트를 쓴 예약이면 자동으로 환불되고, 고객에게 취소 알림도 함께 발송돼요.',
    image: require('../../../assets/guide/faq_ops.jpg'),
  },
  {
    category: 'store',
    q: '정산 내역이나 현재 플랜은 어디서 확인하나요?',
    steps: [
      '마이 탭 → 매장 정보 하단 "매장 요약"에서 현재 플랜 확인',
      '매장관리 탭 → 통계에서 월별 정산 요약 확인 (매장관리자 기능 애드온 필요)',
    ],
    note: '플랜 변경·애드온 신청은 총괄관리자만 할 수 있어요.',
    image: require('../../../assets/guide/faq_plan.png'),
  },
  {
    category: 'customer',
    q: '고객 리뷰에 답변하려면 어떻게 하나요?',
    steps: [
      '매장관리 탭 홈의 "고객 리뷰에 답변하기" 카드 탭',
      '또는 운영관리 탭 → 매장 설정 → 리뷰 답글로 이동',
    ],
    note: null,
    image: null,
  },
  {
    category: 'store',
    q: '매장스태프(직원) 계정은 어떻게 만드나요?',
    steps: [
      '마이 탭 → 본사에 문의하기로 발급 요청',
    ],
    note: '직원 계정은 매출·정산이 보이는 "매장관리" 탭 없이 예약관리·매장설정만 이용할 수 있어요.',
    image: null,
  },
  {
    category: 'booking_settings',
    q: '공휴일에는 예약이 자동으로 막히나요?',
    steps: [
      '운영관리 탭 → 매장 설정 → 공휴일 자동 차단에서 켜짐/꺼짐 확인',
    ],
    note: '기본은 켜짐이라 공휴일은 자동으로 차단돼요. 특정 날짜만 예외로 정상 영업하도록 열어둘 수도 있어요.',
    image: null,
  },
  {
    category: 'booking_ops',
    q: '예약 성공/실패 결과는 어떻게 입력하나요?',
    steps: [
      '운영관리 탭 → 예약 관리로 이동',
      '해당 예약 카드에서 "성공" 또는 "실패" 버튼 탭',
      '잘못 눌렀다면 "초기화"로 되돌리기',
    ],
    note: '결과를 입력해야 매장관리 탭의 성공률·통계에 정확히 반영돼요.',
    image: require('../../../assets/guide/faq_ops.jpg'),
  },
  {
    category: 'booking_settings',
    q: '특정 날짜·요일·시간대에는 예약을 막고 싶어요.',
    steps: [
      '운영관리 탭 → 매장 설정 → 운영시간으로 이동',
      '테마 카드 펼치기',
      '하루만 막으려면 "예약 불가 날짜", 매주 반복이면 "반복 요일", 특정 시간대만 막으려면 "예약 불가 시간대"에서 설정',
    ],
    note: null,
    image: null,
  },
  {
    category: 'booking_settings',
    q: '예약을 며칠 전부터 받을 수 있게 하나요?',
    steps: [
      '운영관리 탭 → 매장 설정 → 운영시간으로 이동',
      '테마 카드를 펼쳐서 "예약 오픈 기간" 설정',
      '며칠 후부터 예약을 받을지 지정',
    ],
    note: '기본값은 무제한(모든 날짜 예약 가능)이에요.',
    image: null,
  },
  {
    category: 'customer',
    q: '고객이 남긴 매장 문의에는 어떻게 답변하나요?',
    steps: [
      '마이 탭 → 고객 문의 답변으로 이동',
      '문의를 눌러 펼친 뒤 답변 입력',
    ],
    note: null,
    image: null,
  },
  {
    category: 'store',
    q: '알림을 실시간으로 받을지, 하루 요약으로 받을지 정할 수 있나요?',
    steps: [
      '마이 탭 → 매장 정보로 이동',
      '"알림 다이제스트" 토글 전환',
    ],
    note: '끄면 예약·리뷰·취소가 생길 때마다 바로 알림이 오고, 켜면 하루에 한 번 요약만 받아요.',
    image: null,
  },
  {
    category: 'store',
    q: '여러 매장(지점)을 관리하는데 화면은 어떻게 전환하나요?',
    steps: [
      '화면 상단 우측의 매장/지점 선택 드롭다운 탭',
      '전환할 매장 또는 지점 선택',
    ],
    note: '매장관리·운영관리·마이 탭 상단에 항상 떠 있어요.',
    image: null,
  },
];

function FaqRow({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <View style={s.card}>
      <TouchableOpacity
        style={s.qRow}
        activeOpacity={0.7}
        onPress={() => setOpen(v => !v)}
        hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }}>
        <Text style={s.qText}>{item.q}</Text>
        <Text style={[s.arrow, open && s.arrowOpen]}>{open ? '▲' : '▼'}</Text>
      </TouchableOpacity>
      {open && (
        <View style={s.aWrap}>
          <View style={s.stepList}>
            {item.steps.map((step, i) => (
              <View key={i} style={s.stepRow}>
                <View style={s.stepBadge}><Text style={s.stepBadgeTxt}>{i + 1}</Text></View>
                <Text style={s.stepText}>{step}</Text>
              </View>
            ))}
          </View>
          {!!item.note && (
            <View style={s.noteBox}>
              <Text style={s.noteText}>{item.note}</Text>
            </View>
          )}
          {item.image && <Image source={item.image} style={s.aImage} resizeMode="contain" />}
        </View>
      )}
    </View>
  );
}

export default function StoreAdminFaqScreen({ navigation }) {
  const insets = useSafeAreaInsets();
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].key);
  const visibleItems = useMemo(
    () => FAQ_ITEMS.filter(item => item.category === activeCategory),
    [activeCategory],
  );

  return (
    <View style={s.container}>
      <AppHeader showBack centerElement={<Text style={{ color: TEXT, fontSize: 15, fontWeight: '800' }}>운영 Q&A</Text>} />
      <View style={s.introBox}>
        <IconLabel Icon={HelpCircleIcon} size={14} color={GOLD} textStyle={s.introTitle}>매장 운영, 궁금한 게 있나요?</IconLabel>
        <Text style={s.introSub}>궁금한 유형을 골라 원하는 질문을 바로 찾아보세요. 답을 못 찾으면 마이 탭 → 본사에 문의하기로 편하게 물어보세요.</Text>
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.tabScroll} contentContainerStyle={s.tabRow}>
        {CATEGORIES.map(cat => {
          const active = activeCategory === cat.key;
          return (
            <TouchableOpacity
              key={cat.key}
              onPress={() => setActiveCategory(cat.key)}
              style={[s.tabChip, active && s.tabChipActive]}
              activeOpacity={0.8}>
              <Text style={[s.tabChipText, active && s.tabChipTextActive]} numberOfLines={1}>{cat.label}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
      <ScrollView contentContainerStyle={{ padding: 16, paddingTop: 12, paddingBottom: insets.bottom + 32 }}>
        {visibleItems.map((item, i) => <FaqRow key={i} item={item} />)}
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: BG },
  introBox: { backgroundColor: GOLD + '12', borderRadius: 10, borderWidth: 1, borderColor: GOLD + '35', padding: 12, marginHorizontal: 16, marginTop: 12 },
  introTitle: { color: GOLD, fontSize: 13, fontWeight: '800' },
  introSub: { color: TEXT_MUTED, fontSize: 12, marginTop: 4, lineHeight: 17 },

  tabScroll: { flexGrow: 0, marginTop: 14 },
  // 80차 후속: [버그 수정, iOS 실기기 신고] 웹에서 겪은 찌그러짐(위 기록)을 flexShrink:0로
  // 막아뒀는데도 iOS에서는 특정 탭을 누르면(활성 칩의 글자가 bold로 바뀌며 폭이 살짝
  // 변함) 다른 칩들의 글자가 잘려 보이는 게 재발했음 — RN의 `gap` 레이아웃 속성이 가로
  // ScrollView 안에서 형제 요소의 내재 크기가 동적으로 바뀔 때 iOS에서 재계산이 어긋나는
  // 사례가 있어(뉴 아키텍처/구 아키텍처 공통으로 보고된 이슈), gap 대신 각 칩에 직접
  // marginRight를 줘 이 레이아웃 타이밍 문제 자체를 피함. numberOfLines={1}(호출부에 추가)
  // 도 함께 걸어 원인이 다른 경우라도 글자가 줄바꿈되어 잘리는 일은 없도록 방어.
  tabRow: { flexDirection: 'row', paddingHorizontal: 16 },
  tabChip: { flexShrink: 0, flexGrow: 0, marginRight: 8, paddingHorizontal: 14, paddingVertical: 9, borderRadius: 999, borderWidth: 1, borderColor: BORDER, backgroundColor: SURFACE2 },
  tabChipActive: { borderColor: GOLD, backgroundColor: GOLD },
  tabChipText: { color: TEXT_MUTED, fontSize: 13, fontWeight: '600' },
  tabChipTextActive: { color: COLORS.onGold, fontWeight: '700' },

  card: { backgroundColor: SURFACE2, borderWidth: 1, borderColor: BORDER, borderRadius: 12, marginBottom: 10, overflow: 'hidden' },
  qRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 14, paddingVertical: 15 },
  qText: { flex: 1, color: TEXT, fontSize: 14, fontWeight: '700', marginRight: 10, lineHeight: 20 },
  arrow: { color: TEXT_MUTED, fontSize: 13 },
  arrowOpen: { color: GOLD },

  aWrap: { paddingHorizontal: 14, paddingBottom: 16, paddingTop: 2, borderTopWidth: 1, borderTopColor: BORDER },

  stepList: { marginTop: 12, gap: 10 },
  stepRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  stepBadge: { width: 20, height: 20, borderRadius: 10, backgroundColor: GOLD + '20', borderWidth: 1, borderColor: GOLD + '55',
               alignItems: 'center', justifyContent: 'center', marginTop: 1 },
  stepBadgeTxt: { color: GOLD, fontSize: 11, fontWeight: '800' },
  stepText: { flex: 1, color: TEXT, fontSize: 13.5, lineHeight: 20 },

  noteBox: { marginTop: 14, backgroundColor: SURFACE, borderRadius: 10, borderLeftWidth: 3, borderLeftColor: GOLD,
             paddingVertical: 10, paddingHorizontal: 12 },
  noteText: { color: TEXT_MUTED, fontSize: 12.5, lineHeight: 19 },

  aImage: { width: '100%', height: 220, borderRadius: 10, marginTop: 14, backgroundColor: SURFACE },
});
